/**
 * XBRL / ESEF Tagging Readiness — spine view over GET /api/v1/xbrl/coverage.
 *
 * Answers one auditor/analyst question: how tag-ready is this reporting cycle
 * for ESEF digital tagging against the ESRS Set 1 XBRL taxonomy? Reads L3
 * resolved disclosures joined to the taxonomy concept map; reports what maps,
 * what is provisional, and — the honest part — what does not map at all.
 *
 * Replaces the pre-spine XBRLExport façade whose /xbrl/taxonomies, /filings,
 * /auto-data and /generate-from-esrs endpoints 404. iXBRL generation itself is
 * Phase 2 (Arelle); this is the readiness gate that precedes it.
 * Spec: memory/XBRL_ESEF_EXPORT_SPEC.md.
 */
import { useState, useEffect, useCallback } from "react";
import {
  FileCheck2, AlertTriangle, CircleSlash, HelpCircle, RefreshCw,
  ScanLine, Ruler,
} from "lucide-react";
import { api, C, Loader, Err, Breadcrumb } from "./journeys/shared";

const MONO = "'JetBrains Mono', ui-monospace, monospace";
// AA-readable secondary ink on the card surface (C.muted is only 3.93:1,
// C.dim 1.80:1 — both fail WCAG AA for normal text). 5.93:1 on #111c30.
const INK2 = "#8a99b5";
const FRAMEWORK = "CSRD_ESRS";
const TAXONOMY = "esrs-set1-2024";

// mapping_status → { color, icon, label } — status is never conveyed by color alone
const STATUS = {
  tag_ready:   { color: C.emerald, Icon: FileCheck2,    label: "Tag-ready" },
  provisional: { color: C.amber,   Icon: HelpCircle,    label: "Provisional" },
  no_concept:  { color: INK2,   Icon: CircleSlash,   label: "No concept" },
  unmapped:    { color: C.red,     Icon: AlertTriangle, label: "Unmapped" },
};

const Select = ({ value, onChange, children, ariaLabel }) => (
  <select aria-label={ariaLabel} value={value} onChange={(e) => onChange(e.target.value)}
    style={{ padding: "7px 10px", borderRadius: 6, border: `1px solid ${C.border}`,
      background: C.surface, color: C.text, fontSize: 12, minWidth: 180, fontFamily: "inherit" }}>
    {children}
  </select>
);

// Fixed context (single framework / single taxonomy release in Phase 1) —
// shown, not hidden, so the scope of the number is legible.
const Fixed = ({ label, value }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
    <span style={{ fontSize: 9, color: INK2, textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</span>
    <span style={{ fontSize: 12, color: C.text, fontFamily: MONO }}>{value}</span>
  </div>
);

function Segment({ kind, count, total }) {
  const s = STATUS[kind];
  const pct = total ? (count / total) * 100 : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
      <s.Icon size={14} strokeWidth={1.5} color={s.color} aria-hidden />
      <span style={{ color: C.text, minWidth: 92 }}>{s.label}</span>
      <span style={{ fontFamily: MONO, color: s.color, fontWeight: 600, minWidth: 34, textAlign: "right" }}>{count}</span>
      <span style={{ fontFamily: MONO, color: INK2, fontSize: 11 }}>{pct.toFixed(0)}%</span>
    </div>
  );
}

function ReadinessPanel({ cov }) {
  const total = cov.total_codes;
  const segs = [
    ["tag_ready", cov.mapped],
    ["provisional", cov.provisional],
    ["no_concept", cov.no_concept],
    ["unmapped", cov.unmapped],
  ];
  const headColor = cov.pct_tag_ready >= 80 ? C.emerald : cov.pct_tag_ready >= 40 ? C.amber : C.red;
  return (
    <section style={{ background: C.card, border: `1px solid ${C.border}44`, borderRadius: 10,
      padding: "20px 22px", marginBottom: 16 }}>
      <div style={{ display: "flex", gap: 28, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ minWidth: 180 }}>
          <div style={{ fontSize: 10, color: INK2, textTransform: "uppercase",
            letterSpacing: "0.5px", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
            <ScanLine size={13} strokeWidth={1.5} aria-hidden /> Tag-ready
          </div>
          <div style={{ fontFamily: MONO, fontSize: 52, fontWeight: 700, lineHeight: 1, color: headColor }}>
            {cov.pct_tag_ready.toFixed(0)}<span style={{ fontSize: 24, color: INK2 }}>%</span>
          </div>
          <div style={{ fontFamily: MONO, fontSize: 11, color: INK2, marginTop: 6 }}>
            {cov.mapped} of {total} datapoints
          </div>
          <div style={{ fontSize: 10, color: INK2, marginTop: 6, maxWidth: 210, lineHeight: 1.5 }}>
            Only hand-reviewed concept mappings count as tag-ready. Provisional
            mappings need review before an ESEF filing.
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 260 }}>
          <div role="img"
            aria-label={`${cov.mapped} tag-ready, ${cov.provisional} provisional, ${cov.no_concept} no concept, ${cov.unmapped} unmapped of ${total} datapoints`}
            style={{ display: "flex", height: 12, borderRadius: 6, overflow: "hidden",
              background: C.dim + "33", marginBottom: 16 }}>
            {segs.map(([k, n]) => n > 0 && (
              <div key={k} style={{ width: `${(n / total) * 100}%`, background: STATUS[k].color,
                transition: "width 0.5s" }} title={`${STATUS[k].label}: ${n}`} />
            ))}
          </div>
          <div style={{ display: "grid", gap: 8 }}>
            {segs.map(([k, n]) => <Segment key={k} kind={k} count={n} total={total} />)}
          </div>
        </div>
      </div>
    </section>
  );
}

function DataPanel({ title, Icon, count, accent, children, empty }) {
  return (
    <section style={{ background: C.card, border: `1px solid ${C.border}44`, borderRadius: 10,
      padding: "16px 18px" }}>
      <header style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <Icon size={15} strokeWidth={1.5} color={accent} aria-hidden />
        <h2 style={{ fontSize: 13, fontWeight: 600, color: C.text, margin: 0 }}>{title}</h2>
        <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color: accent,
          background: accent + "18", padding: "1px 8px", borderRadius: 10 }}>{count}</span>
      </header>
      {count === 0
        ? <div style={{ fontSize: 12, color: INK2, padding: "8px 2px" }}>{empty}</div>
        : children}
    </section>
  );
}

const Row = ({ cells, mono = true }) => (
  <div style={{ display: "flex", gap: 12, padding: "6px 2px", borderBottom: `1px solid ${C.border}22`,
    fontSize: 12, fontFamily: mono ? MONO : "inherit" }}>
    {cells.map((c, i) => <span key={i} style={{ flex: c.flex || 1, color: c.color || C.text,
      textAlign: c.align || "left" }}>{c.v}</span>)}
  </div>
);

export default function XBRLExport() {
  const [entities, setEntities] = useState(null);
  const [cycles, setCycles] = useState(null);
  const [entityId, setEntityId] = useState("");
  const [cycleId, setCycleId] = useState("");
  const [cov, setCov] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([
      api("/api/v1/enterprise/entities").catch(() => ({ entities: [] })),
      api("/api/v1/spine/cycles").catch(() => ({ cycles: [] })),
    ]).then(([e, c]) => {
      const es = e?.entities || [];
      const cs = c?.cycles || [];
      setEntities(es);
      setCycles(cs);
      setEntityId((cur) => cur || es[0]?.entity_id || "");
      setCycleId((cur) => cur || cs[0]?.cycle_id || "");
    });
  }, []);

  const loadCoverage = useCallback(async () => {
    if (!entityId || !cycleId) { setCov(null); return; }
    setLoading(true); setError(null);
    try {
      const r = await api(`/api/v1/xbrl/coverage?entity_id=${encodeURIComponent(entityId)}`
        + `&cycle_id=${encodeURIComponent(cycleId)}&framework=${FRAMEWORK}`
        + `&taxonomy_release=${TAXONOMY}`);
      setCov(r);
    } catch (e) {
      setError(e?.message || "Failed to load tagging coverage");
      setCov(null);
    } finally { setLoading(false); }
  }, [entityId, cycleId]);

  useEffect(() => { loadCoverage(); }, [loadCoverage]);

  if (entities === null) return <div style={{ padding: 24 }}><Loader text="Loading context…" /></div>;

  return (
    <div style={{ padding: "20px 24px", maxWidth: 1080, margin: "0 auto" }}>
      <Breadcrumb path={[{ label: "Frameworks" }, { label: "XBRL Tagging Readiness" }]} />

      <header style={{ marginBottom: 18 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: C.text, margin: "0 0 4px",
          letterSpacing: "-0.02em" }}>XBRL / ESEF Tagging Readiness</h1>
        <p style={{ fontSize: 12, color: INK2, margin: 0, maxWidth: 680, lineHeight: 1.5 }}>
          How much of this cycle&apos;s resolved disclosures can be tagged against
          the EFRAG ESRS Set 1 XBRL taxonomy today. Digital tagging (Inline XBRL)
          is required for the CSRD sustainability statement in ESEF format; this is
          the readiness gate before generation.
        </p>
      </header>

      <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap",
        marginBottom: 16, padding: "12px 16px", background: C.surface,
        border: `1px solid ${C.border}44`, borderRadius: 8 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 9, color: INK2, textTransform: "uppercase", letterSpacing: "0.5px" }}>Entity</span>
          <Select ariaLabel="Entity" value={entityId} onChange={setEntityId}>
            {(entities || []).length === 0 && <option value="">No entities</option>}
            {(entities || []).map((e) => <option key={e.entity_id} value={e.entity_id}>{e.name || e.entity_id}</option>)}
          </Select>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 9, color: INK2, textTransform: "uppercase", letterSpacing: "0.5px" }}>Reporting cycle</span>
          <Select ariaLabel="Reporting cycle" value={cycleId} onChange={setCycleId}>
            {(cycles || []).length === 0 && <option value="">No cycles</option>}
            {(cycles || []).map((c) => <option key={c.cycle_id} value={c.cycle_id}>{c.label || c.cycle_id}</option>)}
          </Select>
        </div>
        <Fixed label="Framework" value={FRAMEWORK} />
        <Fixed label="Taxonomy" value={TAXONOMY} />
        <button onClick={loadCoverage} disabled={loading || !entityId || !cycleId} aria-label="Refresh"
          style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, padding: "7px 14px",
            borderRadius: 6, border: `1px solid ${C.blue}44`, background: C.blue + "18", color: C.blue,
            fontSize: 12, fontWeight: 600, cursor: loading ? "wait" : "pointer" }}>
          <RefreshCw size={13} strokeWidth={1.5} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} aria-hidden />
          Refresh
        </button>
      </div>

      <Err error={error} />

      {loading && !cov && <Loader text="Computing tagging coverage…" />}

      {!loading && !cov && !error && (
        <div style={{ padding: "40px 20px", textAlign: "center", color: INK2, fontSize: 13,
          background: C.card, border: `1px dashed ${C.border}66`, borderRadius: 10 }}>
          Select an entity and reporting cycle to see its ESEF tagging readiness.
        </div>
      )}

      {cov && cov.total_codes === 0 && (
        <div style={{ padding: "40px 20px", textAlign: "center", background: C.card,
          border: `1px solid ${C.border}44`, borderRadius: 10 }}>
          <CircleSlash size={28} strokeWidth={1.5} color={C.muted} style={{ marginBottom: 10 }} aria-hidden />
          <div style={{ fontSize: 14, color: C.text, marginBottom: 4 }}>No resolved disclosures yet</div>
          <div style={{ fontSize: 12, color: INK2, maxWidth: 440, margin: "0 auto", lineHeight: 1.5 }}>
            This cycle has no confirmed or published {FRAMEWORK} datapoints to tag.
            Coverage is computed once disclosures are resolved into the spine.
          </div>
        </div>
      )}

      {cov && cov.total_codes > 0 && (
        <>
          <ReadinessPanel cov={cov} />

          <div style={{ display: "grid", gap: 16,
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" }}>
            <DataPanel title="Unmapped datapoints" Icon={AlertTriangle} accent={C.red}
              count={cov.unmapped_codes.length}
              empty="Every datapoint resolves to a taxonomy concept.">
              <div style={{ fontSize: 11, color: INK2, marginBottom: 8, lineHeight: 1.5 }}>
                Resolved datapoints with no concept in {TAXONOMY}. Each blocks a
                clean filing until the code is mapped or confirmed out of scope.
              </div>
              {cov.unmapped_codes.map((code) => (
                <Row key={code} cells={[{ v: code }]} />
              ))}
            </DataPanel>

            <DataPanel title="Unit mismatches" Icon={Ruler} accent={C.amber}
              count={cov.unit_mismatches.length}
              empty="All units match their taxonomy expectation.">
              <div style={{ fontSize: 11, color: INK2, marginBottom: 8, lineHeight: 1.5 }}>
                Datapoint unit differs from the concept&apos;s expected unit — a
                data-quality issue to resolve before tagging.
              </div>
              <Row mono={false} cells={[
                { v: "Datapoint", color: INK2, flex: 2 },
                { v: "Value unit", color: INK2 },
                { v: "Expected", color: INK2 },
              ]} />
              {cov.unit_mismatches.map((m) => (
                <Row key={m.code} cells={[
                  { v: m.code, flex: 2 },
                  { v: m.unit, color: C.red },
                  { v: m.expected, color: C.emerald },
                ]} />
              ))}
            </DataPanel>
          </div>
        </>
      )}

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}
        @media (prefers-reduced-motion: reduce){*{animation:none!important;transition:none!important}}`}</style>
    </div>
  );
}
